import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation, useParams } from "react-router-dom";

import { createReference } from "@medplum/core";
import { useMedplum, useSubscription } from "@medplum/react";

import { Flex } from "@mantine/core";

import { ORGANIZATION_ID } from "@/constants/local-storage";
import { NavbarContext } from "@/contexts/navbar";
import { SNOMEDCodes } from "@/fhir/enums/snomed";
import { Systems } from "@/fhir/enums/systems";
import { readProvenanceSubscription, threadReplyCriteria } from "@/messages/utils/subscriptions";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { getActiveOrganizationId, joinTokenString } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";

import { TextWrapper } from "../../components/TextWrapper";
import { useNavBarLinks } from "../hooks/useNavBarLinks";
import {
  fetchLabOrdersAwaitingApprovalOrReviewCount,
  fetchUnreadNotificationsCount,
} from "../services/navbar";
import { NavBarLink } from "../types/navbar";

import NavBarButton from "./NavBarButton";
import NavBarItem from "./NavBarItem";
import PracticeSwitch from "./PracticeSwitch";

interface NavBarItemLinkProps {
  link: NavBarLink;
  collapsed?: boolean;
}

/** The side navigation: page links, permission-gated items, and live unread message and lab order counts. */
export default function NavBar() {
  const { collapsed } = useContext(NavbarContext);
  const medplum = useMedplum();
  const [unreadCommunicationsCount, setUnreadCommunicationsCount] = useState<number>(0);
  const [labOrdersCount, setLabOrdersCount] = useState<number>(0);
  const { practiceId } = useParams<{ practiceId: string }>();
  const { hasProviderOrScribeRole } = usePractitionerPermissions();

  const [profile, profileReference] = useMemo(() => {
    const profile = medplum.getProfile();
    if (!profile) return [];
    return [profile, createReference(profile).reference];
  }, [medplum]);

  const handleUnreadCommunicationsCount = useCallback(async () => {
    if (!profile) return;
    setUnreadCommunicationsCount(await fetchUnreadNotificationsCount(medplum, profile, practiceId));
  }, [medplum, profile, practiceId]);

  const handleLabOrdersCount = useCallback(async () => {
    if (!profile) return;
    setLabOrdersCount(
      await fetchLabOrdersAwaitingApprovalOrReviewCount(medplum, profile, practiceId),
    );
  }, [medplum, profile, practiceId]);

  const subscriptionOptions = {
    onSubscriptionConnect: useCallback(async () => {
      try {
        await handleUnreadCommunicationsCount();
      } catch (e) {
        showSystemErrorNotification();
        throw e;
      }
    }, [handleUnreadCommunicationsCount]),
    onError: useCallback((error: Error) => {
      console.error(error);
    }, []),
  };

  const readProvenances = readProvenanceSubscription(profileReference!);

  useSubscription(
    threadReplyCriteria(profileReference!, [profileReference!]),
    handleUnreadCommunicationsCount,
    subscriptionOptions,
  );

  useSubscription(readProvenances.criteria, handleUnreadCommunicationsCount, {
    ...subscriptionOptions,
    subscriptionProps: readProvenances.subscriptionProps,
  });

  useSubscription(
    `ServiceRequest?category=${joinTokenString(Systems.SNOMED, SNOMEDCodes.LaboratoryProcedure)}`,
    handleLabOrdersCount,
    subscriptionOptions,
  );

  useEffect(() => {
    handleUnreadCommunicationsCount();
  }, [handleUnreadCommunicationsCount]);

  useEffect(() => {
    if (!hasProviderOrScribeRole) return;
    handleLabOrdersCount();
  }, [handleLabOrdersCount, hasProviderOrScribeRole]);

  useEffect(() => {
    if (!practiceId) return;
    if (getActiveOrganizationId() === practiceId) return;
    localStorage.setItem(ORGANIZATION_ID, practiceId);
    window.location.reload();
  }, [practiceId]);

  const links = useNavBarLinks({
    unreadCommunicationsCount,
    labOrdersCount,
  });

  return (
    <Flex gap="sm" align="center" direction="column" mt={{ base: 0, sm: "lg" }}>
      <PracticeSwitch />
      {links.map((link) => (
        <NavBarItemLink link={link} key={link.label} collapsed={collapsed} />
      ))}
    </Flex>
  );
}

function NavBarItemLink({ link, collapsed }: NavBarItemLinkProps) {
  const { pathname } = useLocation();

  const isActive = useMemo(
    () =>
      link.activePaths.some((path) => matchPath(path, pathname)) &&
      !link.excludePaths?.some((path) => matchPath(path, pathname)),
    [link, pathname],
  );

  const style = useMemo(() => {
    if (collapsed) {
      return { borderRadius: "var(--mantine-radius-md)" };
    }

    if (isActive) {
      return { borderLeft: "3px solid var(--mantine-color-burgundy-9)", marginLeft: "-6px" };
    }

    return {};
  }, [collapsed, isActive]);

  return (
    <NavBarButton link={link} collapsed={collapsed}>
      <NavBarItem
        style={style}
        key={link.label}
        label={link.label}
        collapsed={collapsed}
        bg={isActive ? "burgundy.1" : "white"}
      >
        {collapsed ? (
          link.icon
        ) : (
          <Flex gap="sm" align="center">
            {link.icon}
            <TextWrapper variant="bold" c="burgundy.9">
              {link.label}
            </TextWrapper>
          </Flex>
        )}
      </NavBarItem>
    </NavBarButton>
  );
}
